import { useEffect } from 'react';
import type { ErrorComponentProps } from '@tanstack/react-router';

import { reportError } from '@/shared/lib/error-reporter';
import { isChunkLoadError, reloadForStaleChunk } from '@/shared/pwa/chunk-recovery';
import common from '@/shared/i18n/en/common';
import messages from '@/shared/i18n/en/ui-route';
import { useT } from '@/shared/i18n/react';

/**
 * Route-level error component (`defaultErrorComponent`). Its main job is to make
 * code-splitting deploy-safe: when a route's JS chunk 404s because a new deploy
 * rotated the content hashes while this tab was open, we transparently reload
 * ONCE — reloading re-fetches index.html → the fresh SW manifest → correct
 * hashes. This is the LAST line of defence: {@link setupChunkRecovery} catches
 * most stale-chunk failures at the `vite:preloadError` stage (before any blank),
 * and both paths share one time-based guard so they can't reload-loop.
 *
 * Auth survives the reload: the access token re-mints from the httpOnly refresh
 * cookie on boot, so the user isn't bounced to /login.
 *
 * Anything that isn't a chunk-load error renders a calm, non-destructive panel
 * (Retry / Reload) — scoped to the route Outlet, so the shell chrome around it
 * stays mounted instead of white-screening.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const chunkMiss = isChunkLoadError(error);
  const t = useT(messages);
  const tc = useT(common);

  // Loader / beforeLoad failures land here without passing a React boundary;
  // render errors were already reported via onCaughtError (deduped by object).
  useEffect(() => {
    if (!chunkMiss) reportError(error, 'boundary');
  }, [chunkMiss, error]);

  useEffect(() => {
    if (!chunkMiss) return;
    // One-shot hard reload to pick up the current asset manifest. The shared
    // guard suppresses a second reload within the cooldown, so a genuinely
    // broken chunk falls through to the panel below instead of looping.
    reloadForStaleChunk();
  }, [chunkMiss]);

  if (chunkMiss) {
    // Reload is firing (or already fired once) — show a quiet placeholder.
    return (
      <div className="grid place-items-center p-10 text-center text-caption text-[var(--fg-muted)]">
        {t('updatingVersion')}
      </div>
    );
  }

  return (
    <div className="grid place-items-center p-10 text-center">
      <div className="max-w-sm space-y-3">
        <p className="text-body font-medium text-[var(--fg)]">{t('pageSnag')}</p>
        <p className="text-caption text-[var(--fg-muted)]">
          {t('pageSnagBody')}
        </p>
        <div className="flex items-center justify-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => reset()}
            className="h-9 px-4 rounded-lg text-caption font-semibold bg-[var(--accent)] text-white active:scale-[0.98] transition-transform"
          >
            {tc('tryAgain')}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="h-9 px-4 rounded-lg text-caption font-medium bg-[var(--bg-muted)] text-[var(--fg)]"
          >
            {t('reload')}
          </button>
        </div>
      </div>
    </div>
  );
}
